// Import Libraries
import React, { useState, useContext } from 'react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';

// Import Components
import UpgradeMembership from '../Modals/Subscription/upgradeMembership.js';

// Import Services
import ScriptService from '../../services/scriptService';

// Context
import { UserContext } from '../../context/User';
import SessionContext from '../../context/Session';

// Reusable Crown Component
const ProCrown = ({ color = 'Gold', customStyle = {} }) => (
  <span className="proCrownWrapper" style={{ ...customStyle }}>
    <FontAwesomeIcon icon={faCrown} className="proImg" style={{ color }} />
  </span>
);

function ProFlag({
  type,
  featureMsg,
  scriptLevel,
  scriptId,
  popoverStyle = {},
  handleUpgrade,
  onModalVisibilityChange,
}) {
  const { user } = useContext(UserContext);
  const [modalIsOpen, setModalIsOpenState] = useState(false);
  // Wraps setModalIsOpen so callers (e.g. a parent modal that needs to hide
  // itself while this upgrade modal is up, since both are separate react-modal
  // instances sharing the same overlay z-index) can react to visibility changes.
  const setModalIsOpen = (open) => {
    setModalIsOpenState(open);
    if (typeof onModalVisibilityChange === 'function') onModalVisibilityChange(open);
  };
  const [purchaseSubModalIsOpen, setPurchaseSubModalIsOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const { setGeneralModalDetails } = useContext(SessionContext);

  function closeMembershipModal(goToFreeTrial) {
    setModalIsOpen(false);
    if (goToFreeTrial) {
      setPurchaseSubModalIsOpen(true);
    }
  }

  const scriptLevelLower = scriptLevel ? scriptLevel.toLowerCase() : '';
  const userLevelLower = user?.level ? user.level.toLowerCase() : '';
  const totalCredits = user?.availableCredits?.totalCredits ?? 0;

  const creditsRemaining = totalCredits ? totalCredits / 100 : 0;

  const upgradeScriptModal = () => {
    setUpgradeDialogOpen(true);
  };

  const handleCloseUpgradeDialog = () => {
    setUpgradeDialogOpen(false);
  };

  const confirmUpgradeScript = async (scriptId, level = 'pro') => {
    await ScriptService.changeScriptLevel(scriptId, level, user.id, true);
    if (typeof handleUpgrade === 'function') {
      handleUpgrade();
    } else {
      console.log('No handleUpgrade function provided to ProFlag');
    }

    toast.success('Your project has been upgraded to Pro successfully!');
    setModalIsOpen(false);
    setGeneralModalDetails({
      open: false,
      header: null,
      message: null,
    });
  };

  const handleConfirmUpgrade = async () => {
    await confirmUpgradeScript(scriptId);
    setUpgradeDialogOpen(false);
  };

  // Helper to wrap any trigger in a well-sized tooltip
  // and make tooltip clicks run the same onClick.
  const withTooltip = (triggerElement, text, onClick) => (
    <Tooltip
      title={<span style={{ whiteSpace: 'normal' }}>{text}</span>}
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 260,
            minWidth: 160,
            whiteSpace: 'normal',
            textAlign: 'center',
            px: 1.5,
            py: 1,
            // allow color/other overrides but avoid width overrides
            ...(() => {
              const { width, minWidth, maxWidth, ...rest } = popoverStyle || {};
              return rest;
            })(),
          },
          // clicking inside the tooltip bubble triggers same handler
          onClick: onClick || undefined,
        },
      }}
    >
      <span
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default', display: 'inline-flex' }}
      >
        {triggerElement}
      </span>
    </Tooltip>
  );

  const renderProFlag = () => {
    // Guard: no user or no levels yet
    if (!user || !userLevelLower || !scriptLevelLower) return null;

    // 1) Free-use Pro feature (non-pro user, freemium/basicplus project)
    if (
      type === 'pro' &&
      (scriptLevelLower.includes('freemium') || scriptLevelLower === 'basicplus') &&
      userLevelLower !== 'pro'
    ) {
      const onClick = () => setModalIsOpen(true);
      return withTooltip(
        <ProCrown color="white" />,
        'Upgrade to continue using this feature.',
        onClick,
      );
    }

    // 2) User-level warning (non-pro user, generic)
    if (type === 'user' && userLevelLower !== 'pro') {
      const onClick = () => setModalIsOpen(true);
      return withTooltip(
        <ProCrown color="gold" />,
        'Only Pro subscribers can export in CSV. Upgrade now to unlock this feature.',
        onClick,
      );
    }

    // 3) Pro user with credits — can convert project to Pro
    if (
      type === 'pro' &&
      userLevelLower === 'pro' &&
      scriptLevelLower !== 'pro' &&
      totalCredits >= 100
    ) {
      const onClick = upgradeScriptModal;
      return withTooltip(
        <ProCrown color="white" customStyle={{ width: '20px' }} />,
        'Convert this project to Pro?',
        onClick,
      );
    }

    // 4) Pro user out of credits
    if (
      type === 'pro' &&
      userLevelLower === 'pro' &&
      scriptLevelLower !== 'pro' &&
      totalCredits < 100
    ) {
      // For the link case, normal link behavior is fine; no extra onClick needed
      return withTooltip(
        <a href="https://www.shotkraft.com/contact-us" target="_blank">
          <ProCrown color="gold" />
        </a>,
        `All Pro credits used for this month.
        No worries! Click the icon to message us and we'll add more.`,
      );
    }

    // 5) Non-pro user, non-pro project (generic upsell)
    if (
      type === 'pro' &&
      userLevelLower !== 'pro' &&
      scriptLevelLower !== 'pro' &&
      !scriptLevelLower.includes('freemium') &&
      scriptLevelLower !== 'basicplus'
    ) {
      const onClick = () => setModalIsOpen(true);
      return withTooltip(
        <ProCrown color="gold" />,
        featureMsg || 'You ran out of credits, click here to upgrade to Pro.',
        onClick,
      );
    }

    return null;
  };

  return (
    <>
      {/* MUI Convert Script → Pro Dialog */}
      <Dialog open={upgradeDialogOpen} onClose={handleCloseUpgradeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 3, backgroundColor: '#F3F1FE' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Purple Icon Circle */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#7A64EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AutoAwesomeIcon sx={{ color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h6">Go Pro?</Typography>
                <Typography variant="body1" className="purpleText">
                  You&apos;ve got {creditsRemaining} pro projects available this month!
                </Typography>
              </Box>
            </Box>

            <IconButton size="small" onClick={handleCloseUpgradeDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ pt: 3 }}>
            Convert this project to Pro to unlock advanced features:
          </Typography>

          <List dense>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" sx={{ color: '#7A64EB' }} />
              </ListItemIcon>
              <ListItemText primary="Shotlist your project in one click" />
            </ListItem>

            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" sx={{ color: '#7A64EB' }} />
              </ListItemIcon>
              <ListItemText primary="Use special fields such as VFX" />
            </ListItem>

            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" sx={{ color: '#7A64EB' }} />
              </ListItemIcon>
              <ListItemText primary="Export in tabular format (CSV)" />
            </ListItem>
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseUpgradeDialog}
            fullWidth
            variant="outlined"
            sx={{
              border: '1px solid #7A64EB',
              color: '#7A64EB',
              backgroundColor: '#FAF9FF',
              '&:hover': { backgroundColor: '#F1EEFF' },
            }}
          >
            Cancel
          </Button>

          <Button onClick={handleConfirmUpgrade} variant="contained" fullWidth color="primary">
            Convert Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Existing Upgrade Membership Modal */}
      <UpgradeMembership
        isOpen={modalIsOpen}
        onRequestClose={closeMembershipModal}
        featureMsg={``}
        scriptId={scriptId}
      />

      <span className="logoSpaceHolder">&nbsp;</span>

      {user && user.level && scriptLevel ? (
        <span className="popoverParent">{renderProFlag()}</span>
      ) : null}
    </>
  );
}

export default ProFlag;
